import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import CountUp from 'react-countup'
import AuthContext from 'context/AuthContext'
import { apiGet } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import formsApi from 'utils/pathObj'
import DocumentViewer from 'pages/submitted_forms_by_employee/DocumentViewer'
import AnalyticsTab from './AnalyticsTab'
import RecordsTab from './RecordsTab'

const PAGE = { page: 1, pageSize: 10, sortBy: 'createdAt', sortType: 'desc' }
const TOTALS = { page: 1, pageSize: 1, sortBy: 'createdAt', sortType: 'desc' }

const TABS = [
  { id: 'analytics', label: 'Analytics' },
  { id: 'documents', label: 'Employee documents' },
  { id: 'training', label: 'Training' },
  { id: 'certification', label: 'Certifications' },
]

function readPage(result, pick) {
  if (result.status !== 'fulfilled' || !result.value?.data?.success) {
    return { docs: [], total: 0 }
  }
  return pick(result.value.data.results || {})
}

function Compilence() {
  const { updatePageName, user } = useContext(AuthContext)
  const { t } = useTranslation()

  const [loading, setLoading] = useState(true)
  const [credentialsLoading, setCredentialsLoading] = useState(true)
  const [documentTotal, setDocumentTotal] = useState(0)
  const [categoryTotal, setCategoryTotal] = useState(0)
  const [hiringTotal, setHiringTotal] = useState(0)
  const [onboardingTotal, setOnboardingTotal] = useState(0)
  const [submittedTotal, setSubmittedTotal] = useState(0)
  const [employeeDocuments, setEmployeeDocument] = useState([])
  const [credentialRecords, setCredentialRecords] = useState([])
  const [trainingAssignments, setTrainingAssignments] = useState([])
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [activeTab, setActiveTab] = useState('analytics')

  useEffect(() => {
    updatePageName(t('COMPILENCE_BOARD'))
  }, [])

  const loadPage = (url, params, pageNo, collected) =>
    apiGet(url, { ...params, page: pageNo, pageSize: 100 }).then((result) => {
      const payload = result?.data?.success ? result.data.results || {} : {}
      const docs = payload.docs || []
      const merged = collected.concat(docs)
      const total = Number(payload.totalDocs) || merged.length
      if (merged.length < total && docs.length && pageNo < 20) return loadPage(url, params, pageNo + 1, merged)
      return merged
    })

  const loadCredentials = () => {
    setCredentialsLoading(true)
    Promise.all([
      loadPage(apiPath.trainingCertificationList, { type: 'certification' }, 1, []).catch(() => []),
      loadPage(apiPath.trainingAssignments, {}, 1, []).catch(() => [])
    ])
      .then(([certs, assignments]) => {
        setCredentialRecords(certs)
        setTrainingAssignments(assignments)
      })
      .finally(() => setCredentialsLoading(false))
  }

  useEffect(() => {
    loadCredentials()
  }, [])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      const [docsRes, categoryRes, hiringRes, onboardingRes, submittedRes] = await Promise.allSettled([
        apiGet(apiPath.documentList, TOTALS),
        apiGet(apiPath.categoryList, TOTALS),
        apiGet(formsApi.getForms, { ...TOTALS, categoryType: 'hiring', createdBy: user?.companyID || '' }),
        apiGet(formsApi.getForms, { ...TOTALS, categoryType: 'onboarding', createdBy: user?.companyID || '' }),
        apiGet(apiPath.submitFormByEmployee, { ...PAGE, pageSize: 100 })
      ])

      const totalOf = (res) =>
        readPage(res, (results) => ({ docs: results.docs || [], total: results.totalDocs || 0 }))
      const formsSubmitted = readPage(submittedRes, (results) => ({
        docs: results.finalData?.docs || [],
        total: results.finalData?.totalDocs || 0
      }))

      setDocumentTotal(totalOf(docsRes).total)
      setCategoryTotal(totalOf(categoryRes).total)
      setHiringTotal(totalOf(hiringRes).total)
      setOnboardingTotal(totalOf(onboardingRes).total)
      setEmployeeDocument(
        (formsSubmitted.docs || []).flatMap((item) =>
          (item.EmployeeDocuments || []).map((doc) => ({
            ...doc,
            employeeName: item?.Employee?.name,
            employeeCode: item?.Employee?.employeeId
          }))
        )
      )
      setSubmittedTotal(formsSubmitted.total)
      setLoading(false)
    }
    load()
  }, [user?.companyID])

  const tabTotals = {
    documents: employeeDocuments.length,
    training: trainingAssignments.length,
    certification: credentialRecords.filter((i) => i.type === 'certification').length,
    analytics: null
  }

  return (
    <div className='md:px-8 py-4 px-4'>
      {/* Tab bar */}
      <div className='bg-white border border-slate-200 rounded-lg mb-6'>
        <div className='px-4 pt-2'>
          <ul className='border-b flex flex-wrap'>
            {TABS.map((tab) => (
              <li key={tab.id} className='mr-2'>
                <button
                  type='button'
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 py-3 px-5 text-sm xl:text-base font-semibold border-b-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'analytics' ? (
        <AnalyticsTab
          totals={{ documentTotal, categoryTotal, hiringTotal, onboardingTotal, submittedTotal }}
          employeeDocuments={employeeDocuments}
          credentialRecords={credentialRecords}
          trainingAssignments={trainingAssignments}
          credentialsLoading={credentialsLoading || loading}
        />
      ) : (
        <RecordsTab
          key={activeTab}
          type={activeTab}
          employeeDocuments={employeeDocuments}
          trainingAssignments={trainingAssignments}
          credentialRecords={credentialRecords}
          loading={activeTab === 'documents' ? loading : credentialsLoading}
          onViewDocument={setSelectedDocument}
        />
      )}

      {selectedDocument && (
        <DocumentViewer document={selectedDocument} onClose={() => setSelectedDocument(null)} />
      )}
    </div>
  )
}

export default Compilence